import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';

import { CerRandom } from '@cer/cer-util/cer-random';
import { ApplicationStateVm, ProgressDto, RoleTypeEnum, UserClient, UserSettingDto, UserStateVm, UserStatusClient, UserStatusVm, UserVm } from 'src/app/api';
import { CerAuthorizeService } from 'src/cer-identity/cer-authorize.service';
import { CerSessionEnvironmentService } from './cer-session-environment.service';
import { CerSessionUserService } from './cer-session-user.service';
import { CerSessionUserNavigationService } from './cer-session-user-navigation.service';
import { CerSessionUsersService } from './cer-session-users.service';

export type CerSessionStateType = 'inactive' | 'active' | 'paused' | 'expired' | 'noAccess' | 'noRolesActive';

export class CerRouteAccessResult {
  public route: ActivatedRouteSnapshot;
  public hasAccess: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CerSessionService implements OnDestroy {

  public isAuthenticated$ = new BehaviorSubject<boolean>(false);
  public sessionState$ = new BehaviorSubject<CerSessionStateType>('inactive');

  public settings$ = new BehaviorSubject<UserSettingDto>(null);
  public userStatusVm$ = new BehaviorSubject<UserStatusVm>(null);
  public progress$ = new BehaviorSubject<ProgressDto>(null);

  private subscriptionManager$: Subscription = new Subscription();

  constructor(private authService: CerAuthorizeService,
    private userClient: UserClient,
    private userStatusClient: UserStatusClient,
    public environment: CerSessionEnvironmentService,
    public user: CerSessionUserService,
    public userNavigation: CerSessionUserNavigationService,
    public users: CerSessionUsersService) {
    //this.subscriptionManager$.add(
    //  this.authorizeService.user$.subscribe(oidcUser => this.oidcUserChange(oidcUser))
    //);

    //this.subscriptionManager$.add(
    //  this.authorizeService.authenticationResultStatusSubject.subscribe(loginStatus => this.onAuthenticationResultStatus(loginStatus))
    //);

    this.subscriptionManager$.add(
      this.sessionState$.subscribe(state => {/*this.onStateChanged(state)*/ })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptionManager$.unsubscribe();
  }

  public relogin(returnUrl: string) {
    this.deactivate();
    this.authService.navigateToLogin(returnUrl);
  }

  public forgetAuthCookie() {
    //this.cookieService.deleteCookie('.AspNetCore.Identity.Application');
  }

  public deactivate() {
    this.onUserVm(null);
    this.authService.navigateToLogout();
  }

  public pause() {
    this.sessionState$.next('paused');
  }

  public expire() {
    this.sessionState$.next('expired');
  }

  public isAuthenticated(): boolean {
    return this.isAuthenticated$.getValue() ?? false;
  }

  public onProgress(progressData: ProgressDto) {
    this.progress$.next(progressData);
  }

  public async userVmGet(versionReload: boolean = false): Promise<UserVm> {
    return new Promise<UserVm>((resolve, reject) => {
      this.userClient.getAllUsers().subscribe({
        next: vm => {
          if (versionReload) {
            vm.applicationState.environment.versionReload = Number.parseInt(CerRandom.AsString(5));
          }
          if (this.onUserVm(vm) == true) {
            this.userStatusClient.get().subscribe({
              next: userStatusVm => {
                this.onUserStatusVm(userStatusVm)
              }
            });
            resolve(vm);
          }
          else {
            reject(null);
          }
        },
        error: error => {
          this.onUserVm(null);
          reject(error);
        }
      })
    });
  }

  private onUserVm(userVm: UserVm) {
    if (userVm?.applicationState != null) {
      this.onApplicationState(userVm.applicationState);
    }
    if (userVm?.userState != null) {
      return this.onUserState(userVm.userState);
    }
    return false;
  }

  public onUserStatusVm(userStatus: UserStatusVm) {
    if (userStatus?.applicationState != null) {
      this.onApplicationState(userStatus.applicationState);
    }
    this.userStatusVm$.next(userStatus);
  }

  private onUserState(userState: UserStateVm): boolean {
    if (userState.roles != null) {
      this.user.roles$.next(userState.roles);
    }
    
    const isAuthenticated: boolean = userState.loggedIn == true;
    const hasRoles = this.user.hasRoles();
    this.onIsAuthenticated(isAuthenticated, hasRoles);

    if (userState.entities != null) {
      this.user.entities$.next(userState.entities);
    }

    if (userState.notificationMessages != null) {
      this.user.notifications$.next(userState.notificationMessages);
    }

    if (userState.documentLibs != null) {
      this.user.documentLibs$.next(userState.documentLibs);
    }

    if (userState.user != null || !isAuthenticated) {
      this.user.user$.next(userState.user);
      this.userNavigation.navigationLoad(userState.roles, userState.documentLibs);
    }

    if (userState.routes != null) {
      this.userNavigation.onUserRoutes(userState.routes, userState.userRoutes, userState.userRouteViews);
    }

    return isAuthenticated && this.user.hasRoles();
  }

  private onIsAuthenticated(isAuthenticated: boolean, hasRoles: boolean) {
    this.isAuthenticated$.next(isAuthenticated);
    this.user.isAuthenticated = isAuthenticated;
    this.sessionState$.next(isAuthenticated ? (hasRoles ? 'active' : 'noRolesActive') : 'noAccess');
  }

  public hasRouteAccess(route: ActivatedRouteSnapshot): CerRouteAccessResult {
    var res: CerRouteAccessResult = { route: route, hasAccess: true };
    if (!this.validateStateActive()) {
      res.hasAccess = false;
    }

    if (route != null && res.hasAccess) {
      const requiredRoles: RoleTypeEnum[] = (<any>route?.data)?.roles;
      if (requiredRoles?.length > 0) {

        if (!this.hasUserRoles(requiredRoles)) {
          res.hasAccess = false;
        }
      }
      route.children?.forEach(rChild => {
        if (res.hasAccess) {
          res = this.hasRouteAccess(rChild);
        }
      });
    }
    return res;
  }

  private sessionState(): CerSessionStateType {
    return this.sessionState$.getValue();
  }

  public validateStateActive(): boolean {
    return (this.sessionState() == 'active');
  }

  public hasUserRole(requiredRole: RoleTypeEnum): boolean {
    return this.hasUserRoles([requiredRole]);
  }

  public hasUserRoles(anyOfRequiredRoles: RoleTypeEnum[]): boolean {
    return this.validateUserHasRole(anyOfRequiredRoles);
  }

  public validateUserHasRole(anyOfRequiredRoles: RoleTypeEnum[]): boolean {
    return this.user.hasRole(anyOfRequiredRoles);
  }

  public onApplicationState(state: ApplicationStateVm): void {
    if (state.dataTypes != null) {

    }
    if (state.users != null) {
      this.users.onUsers(state.users)
    }

    if (state.environment != null) {
      this.environment.onEnvironment(state.environment);
    }

    if (state.modules != null) {

    }

    if (state.settings != null) {
      this.settings$.next(state.settings);
    }

    if (state.requestUserState == true) {
      this.userVmGet();
    }
  }

  /*
  private apiGetAppUser(userVm: Vum): Promise<AppUser> {
    return new Promise<AppUser>((resolve, reject) => {
      if (sessionUser) {
        this.userClient.get().subscribe({
          next: vm => {
            if (vm.user != null) {
              var user: AppUser = new AppUser();
              user.sessionUser = sessionUser;
              user.dto = vm.user;
              user.roles = vm.roles;
              user.vm = vm;
              this.coreUserService.metaRead(vm);
              resolve(user);
            }
            else {
              resolve(null);
            }
          },
          error: error => {
            resolve(null);
            this.snackBar.open(error);
            console.error(error);
            this.snackBar.open('Log venligt ind igen');
            this.router.navigate(['/authentication/logout']);
          }
        });
      }
      else {
        resolve(null);
      }
    });
  }
  */

  /*
  private waitForUserExpiry() {
    var oidcUser: User = this.oidcUser$.getValue();
    if(oidcUser != null) {
      var waitTime = (oidcUser.expires_in - 60) * 1000;  // 1 minute before expiry
      setTimeout(() => {
        this.checkUserExpiry();
      },
        waitTime);
    }
  }

  public ping() {
  }

  private checkUserExpiry() {
    var state: string = this.state$.getValue();
    if (state != null && state != 'inactive') {
      var oidcUser = this.oidcUser$.value;
      if (oidcUser != null) {
        if (oidcUser.expires_in <= 5 * 60) {  // 5 minutes before expiry
          this.expire();
        }
        else {
          this.waitForUserExpiry();  // Make sure we check again
        }
      }
    }
  }
  */
}
